import { useEffect, useState, type ReactNode } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { useServices } from './services';
import { Button } from '../components/ui';
import { ThemeSwitch } from '../components/ThemeSwitch';
import { SURFACE } from '../lib/surface';
import { Icon, type IconName } from '../components/Icon';
import wordmarkOnDark from '@antreja/ui/assets/antreja-wordmark-dark.svg';
import wordmarkOnLight from '@antreja/ui/assets/antreja-wordmark-light.svg';
import markOnDark from '@antreja/ui/assets/antreja-icon-dark.svg';

export interface NavSection {
  title: string;
  items: { to: string; label: string; count?: number; countLabel?: string; icon?: IconName }[];
}

/**
 * Het woordmerk uit de brandkit-master (nooit opnieuw getypt). `on` is de achtergrond waarop het staat.
 * Brandbook: minimaal 120 CSS-px artwork; de SVG bevat zelf al een kleine technische marge.
 */
export function Wordmark({ on = 'dark', width = 152 }: { on?: 'dark' | 'light'; width?: number }) {
  return (
    <img
      className="wordmark"
      src={on === 'dark' ? wordmarkOnDark : wordmarkOnLight}
      width={width}
      height={Math.round((width * 191) / 408)}
      alt="Antreja"
    />
  );
}

export function DemoBanner() {
  const { mode } = useServices();
  if (mode !== 'demo') return null;
  return (
    <div className="banner banner-demo" role="note">
      <strong>Demo</strong>
      <span>Fictieve gegevens van VV Demo. Er is geen verbinding met Supabase of met echte sloten.</span>
    </div>
  );
}

export function Shell({
  sections,
  clubSwitch,
  banners,
  children,
  mobileTitle,
}: {
  sections: NavSection[];
  clubSwitch?: { label: string; name: string; to: string };
  banners?: ReactNode;
  children: ReactNode;
  mobileTitle: string;
}) {
  const { session, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <div className="shell">
      <a className="skip-link" href="#main">
        Naar inhoud
      </a>
      <aside className={`sidebar ${open ? 'open' : ''}`} id="sidebar" aria-label="Hoofdmenu">
        <Link to="/" className="brand">
          <Wordmark on="dark" />
          <span className="brand-sub">{SURFACE === 'admin' ? 'admin' : 'beheer'}</span>
        </Link>
        {clubSwitch && (
          <Link to={clubSwitch.to} className="club-switch">
            <small>{clubSwitch.label}</small>
            <strong>{clubSwitch.name}</strong>
          </Link>
        )}
        {sections.map((s) => (
          <nav className="nav-section" key={s.title} aria-label={s.title}>
            <h2>{s.title}</h2>
            <ul className="nav-list">
              {s.items.map((i) => (
                <li key={i.to}>
                  <NavLink to={i.to}>
                    {i.icon ? <Icon name={i.icon} /> : <span className="icon" aria-hidden="true" />}
                    <span className="nav-label">{i.label}</span>
                    {i.count ? (
                      <span className="nav-count" aria-label={i.countLabel ?? String(i.count)}>
                        {i.count}
                      </span>
                    ) : null}
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>
        ))}
        <div className="sidebar-foot">
          <span className="row" style={{ gap: 10, flexWrap: 'nowrap' }}>
            <Icon name="persoon" />
            <strong>{session?.profile.full_name}</strong>
          </span>
          <Button variant="ghost" size="sm" className="btn-on-dark" onClick={() => void signOut()}>
            Uitloggen
          </Button>
          <ThemeSwitch onDark />
        </div>
      </aside>
      <div className="main">
        <div className="topbar">
          <Button
            variant="ghost"
            aria-expanded={open}
            aria-controls="sidebar"
            onClick={() => setOpen((o) => !o)}
            className="btn-on-dark"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M3 6h18M3 12h18M3 18h18" stroke="currentColor" strokeWidth="2" />
            </svg>
            <span className="sr-only">Menu</span>
          </Button>
          <span className="club-name">{mobileTitle}</span>
          <img className="topbar-mark" src={markOnDark} width={28} height={28} alt="Antreja" />
        </div>
        <DemoBanner />
        {banners}
        <main id="main" className="content" tabIndex={-1}>
          {children}
        </main>
      </div>
      {open && <div aria-hidden="true" onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 45 }} />}
    </div>
  );
}
